import type { Grade } from "@ts/interfaces/student/studentAccountInterface";
import { Box, Flex, Text } from "@chakra-ui/react";
import {
  BaseModal,
  Button,
  MessageBox,
  Tag,
  getTrackingId,
  type ModalRef
} from "@euka-mfes/bark";
import { LearningClassEnums as CLS } from "@/ts/enums/LearningClassEnums";
import type { Ref } from "react";
import { Trans } from "react-i18next";
import { GRADE_STATUS } from "@ts/enums/GradeStatus";

interface SelectGradeModalProps {
  modalRef: Ref<ModalRef>;
  grades: Grade[];
  handleGradeSelect: (selectedGrade: Grade) => void;
  errorMessage?: string;
  handleGradeChangeCancel: () => void;
  isOtpLoading?: boolean;
  isProgrammaticCloseRef?: React.MutableRefObject<boolean>;
}

const SelectGradeModal = ({
  modalRef,
  grades,
  handleGradeSelect,
  errorMessage,
  handleGradeChangeCancel,
  isOtpLoading,
  isProgrammaticCloseRef
}: SelectGradeModalProps) => {
  const showErrorMessage = () => {
    if (errorMessage !== "") {
      return <MessageBox type="error">{errorMessage}</MessageBox>;
    }
  };

  const handleClose = () => {
    handleGradeChangeCancel();
    (modalRef as React.RefObject<ModalRef>)?.current?.dismiss?.();
  };

  return (
    <BaseModal
      ref={modalRef}
      title={<Trans>Select grade</Trans>}
      secondaryBtnTxt={<Trans>Cancel</Trans>}
      size="lg"
      shouldHidePrimaryBtn
      infoBanners={showErrorMessage()}
      onSecondaryBtnClick={handleClose}
      resetModalStateOnClose={() => {
        if (!isProgrammaticCloseRef?.current) {
          handleGradeChangeCancel();
        }
        if (isProgrammaticCloseRef) isProgrammaticCloseRef.current = false;
      }}
      isLoading={isOtpLoading}
    >
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        p="48px"
        bg="canvas"
        data-testid="select-grade-modal"
      >
        <Flex gap={2} flexDir="column">
          <Text textStyle="body.standard" textAlign="justify" mb="25px">
            <Trans>
              You have the following Grades available in your Learning Portal,
              select one to view that grade’s content:
            </Trans>
          </Text>
          {grades.length > 0 &&
            grades.map(grade => (
              <Flex
                key={grade.grade_code}
                p="16px"
                border="1px solid"
                borderRadius="8px"
                borderColor="lines"
                backgroundColor="canvas"
                alignItems="center"
                justifyContent="space-between"
                _hover={{
                  cursor: "pointer",
                  backgroundColor: "canvas.dim"
                }}
                onClick={() => handleGradeSelect(grade)}
                data-tracking-id={getTrackingId(
                  [CLS.SelectGradeModal, CLS.Card],
                  String(grade.grade_code)
                )}
              >
                <Text
                  textStyle="body.standard"
                  textAlign="left"
                  color="text.action"
                >
                  {grade.name}
                </Text>
                {grade.status === GRADE_STATUS.CURRENT && (
                  <Tag
                    isShowIcon={false}
                    textStyle="folio.standard"
                    fontWeight={500}
                    p="6px 14px 6px 12px"
                    status="success"
                  >
                    <Trans>Current</Trans>
                  </Tag>
                )}
                {grade.status === GRADE_STATUS.NOT_STARTED && (
                  <Button
                    variant="tertiary"
                    minW="61px"
                    data-tracking-id={getTrackingId(
                      [CLS.SelectGradeModal, CLS.Button],
                      "start-grade"
                    )}
                  >
                    Start
                  </Button>
                )}
              </Flex>
            ))}
        </Flex>
      </Box>
    </BaseModal>
  );
};

export default SelectGradeModal;
